// Air manual quote — alternate UI matching the provided HTML/CSS mock
// (single set of charges + itinerary segments per airline, combined
// origin/destination charge table with a per-row type toggle, sticky
// footer totals, all-airlines summary table). Kept as its own page/file —
// ManualQuote/index.js (the Formik + flightOptions[] implementation) is
// untouched. Submits through the same addManualQuote/editManualQuote API
// contract as index.js, treating each airline as a single flight option.
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
  Plus,
  X,
  Trash2,
  Lock,
  Plane,
  Package,
  Weight,
  Zap,
  Clock,
} from "lucide-react";

import { MainContainer } from "../../StyledComponents/Dashboard";
import CustomeHeader from "../Admin/Common/CustomeHeader";
import QueryDetailHeader from "./ManualQuoteHeader";
import SelectMultipleWithSearch from "../Admin/Common/SelectMultipleWithSearch";
import SelectItem from "../Admin/Common/SelectItem";
import CopyFromMenu from "./CopyFromMenu";
import { money } from "./chargeUtils";
import {
  AIR_CHARGE_UNIT_TYPE,
  ORIGIN_DESTINATION_CHARGE_TYPE,
  SPACE_AVAILABILITY_OPTIONS,
} from "../../utils/constant";
import { handleKeyPress } from "../../functions/Utils";
import {
  addManualQuote,
  clearManualQuote,
  editManualQuote,
  getAllOriginDestinationCharges,
  getManualQuoteHistory,
} from "../../store/ManualQuote/ManualQuoteSlice";
import {
  getAirlinesList,
  getAirlinesListByCode,
} from "../../store/Markup/markupSlice";
import { getallairports } from "../../store/SearchRates/SearchRatesSlice";
// NOTE: store/Admin/Common/CommonSlice is never registered in the app's
// redux store (src/store/store.js only mounts store/Common/CommonSlice under
// "commonapi") — dispatching its updatenotificationdata is a silent no-op, so
// use the actual registered slice that <Notification/> in App.js reads from.
import { updatenotificationdata } from "../../store/Common/CommonSlice";

// ── Look & feel pulled from the HTML mock ──────────────────────────────────
const ACCENT = "#F5A623";
const ACCENT_DARK = "#B57400";
const CARD_BORDER = "#E7E1D5";
const ROW_BORDER = "#EFEAE0";
const TINT_BORDER = "#F6E7C4";

const LABEL_CLS =
  "block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1";
const INPUT_CLS =
  "h-[36px] px-3 focus:outline-none block w-full text-sm border border-gray-200 rounded-lg text-gray-800 bg-white focus:border-yellow-400";
const READONLY_CLS =
  "h-[36px] px-3 flex items-center text-sm border border-gray-100 rounded-lg text-gray-500 bg-gray-50";

// Sizes the MUI DateTimePicker's TextField to match INPUT_CLS (36px, rounded,
// same border/font as the rest of the form's inputs).
const DATE_PICKER_SX = {
  width: "100%",
  "& .MuiInputBase-root": {
    height: 36,
    fontSize: 13,
    borderRadius: "8px",
    background: "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
};

// Dev-only convenience: type this exact value into the first Origin charge's
// Rate field to reveal an "Autofill" button that fills carrier, expiry,
// charges and itinerary with sample data. Enabled for local dev builds
// (NODE_ENV !== "production") and on the "dev." deployment host — every
// deployed build is a production build regardless of which server it's
// pushed to, so NODE_ENV alone can't tell dev.send-asap.ai apart from the
// real production domain. Never enabled anywhere else.
const DEV_AUTOFILL_TRIGGER = "943065";
const IS_DEV_HOST =
  typeof window !== "undefined" && /^dev\./i.test(window.location.hostname);
const DEV_AUTOFILL_ENABLED =
  process.env.NODE_ENV !== "production" || IS_DEV_HOST;

// These charge records represent "Air Freight" — that charge belongs under
// Freight & Surcharges only, never under Origin/Destination, even though the
// backend serves all three sections from the same shared charge list.
const AIR_FREIGHT_CHARGE_IDS = [
  "67bd9b469e51a4a856d8c24b",
  "67bd9b469e51a4a856d8c6c1",
  "67bd9b469e51a4a856d8c76a",
];
const AIR_FREIGHT_BLOCK_MESSAGE =
  "Air Freight cannot be selected under Origin & Destination charges — add it under Freight & Surcharges instead.";
const AIR_FREIGHT_DUPLICATE_MESSAGE =
  "Only one Air Freight charge can be selected under Freight & Surcharges.";
const FREIGHT_ROW_ISSUE_MESSAGE =
  "Fix the charge rows before updating — only one Air Freight charge is allowed";
// The charge master can grow new "Air Freight"-flavoured entries (e.g. "AIR
// FREIGHT RECEIVED") that aren't in the hardcoded ID list above, which let
// them slip past the "only one Air Freight charge" rule entirely. Catch those
// too by matching the charge's own name, on top of the known IDs.
const isAirFreightLabel = (label) => /air\s*freight/i.test(label || "");
const getAirFreightChargeIds = (chargeList) => {
  const ids = new Set(AIR_FREIGHT_CHARGE_IDS);
  (chargeList || []).forEach((c) => {
    if (isAirFreightLabel(c.label)) ids.add(c.value);
  });
  return ids;
};

const SERVICE_TYPES = [
  { value: "General Cargo", label: "General Cargo" },
  { value: "Express Cargo", label: "Express Cargo" },
  { value: "Priority", label: "Priority" },
];

const ADD_SECTION_BTN_STYLE = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "11px 0",
  borderRadius: 12,
  border: `2px dashed ${ACCENT}`,
  background: "transparent",
  color: ACCENT_DARK,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

// ── Data shapes — charges live on the airline (one price for the whole
// airline); an airline can carry several flight options (different
// schedules/itineraries), each priced identically to the others. ──────────
const makeODRow = (type) => ({ type, charge: "", unit: "perkg", unitRate: "" });

const makeFreightRow = () => ({ charge: "", unit: "perkg", unitRate: "" });

const makeSegment = (carrierCode = "", origin = "", operationType = "") => ({
  carrier: carrierCode,
  operationType,
  origin,
  destination: "",
  departure: null,
  arrival: null,
  aircraft: "",
  flight: "",
});
// LAT/TOA are priced/scheduled once per flight option (the whole itinerary),
// not per leg — every segment shares the same value.
const makeFlightOption = (carrierCode = "", origin = "") => ({
  lat: "",
  toa: "",
  segments: [makeSegment(carrierCode, origin)],
});
const makeAirline = () => ({
  carrierCode: "",
  carrierName: "",
  serviceType: SERVICE_TYPES[0].value,
  spaceAvailability: SPACE_AVAILABILITY_OPTIONS[0].value,
  odCharges: [makeODRow("origin")],
  freightCharges: [makeFreightRow()],
  flightOptions: [makeFlightOption()],
});

// ── Math helpers (mirrors chargeUtils.js's rowTotal, adapted to this shape) ─
const rowAmount = (row, weight) => {
  const rate = Number(row?.unitRate) || 0;
  return row?.unit === "perkg" ? rate * (Number(weight) || 0) : rate;
};
const sumRows = (rows, weight) =>
  (rows || []).reduce((sum, row) => sum + rowAmount(row, weight), 0);
const odAmount = (odCharges, type, weight) =>
  sumRows(
    (odCharges || []).filter((row) => row.type === type),
    weight,
  );
const airlineTotals = (airline, weight) => {
  const origin = odAmount(airline?.odCharges, "origin", weight);
  const destination = odAmount(airline?.odCharges, "destination", weight);
  const freight = sumRows(airline?.freightCharges, weight);
  return {
    origin,
    destination,
    freight,
    grand: origin + destination + freight,
  };
};

const minutesBetween = (a, b) =>
  a && b ? Math.round((new Date(b) - new Date(a)) / 60000) : 0;
const durationLabel = (min) => {
  if (!(min > 0)) return "—";
  return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}m`;
};
const airportCode = (str) => {
  if (!str) return "—";
  const match = /\(([^)]+)\)/.exec(str);
  return match ? match[1] : str;
};
const withLockedEnds = (segments, originAirport, destAirport) =>
  (segments || []).map((seg, i, arr) => ({
    ...seg,
    ...(i === 0 ? { origin: originAirport || seg.origin } : {}),
    ...(i === arr.length - 1
      ? { destination: destAirport || seg.destination }
      : {}),
  }));
// Aircraft is optional — every other itinerary field is required.
const isItineraryComplete = (segments) =>
  segments?.length > 0 &&
  segments.every(
    (s) =>
      s.carrier &&
      s.origin &&
      s.destination &&
      s.departure &&
      s.arrival &&
      s.flight,
  );

// ── Submit payload — same airlines[].flightOptions[] contract as index.js ──
// The carrier/origin/destination selects store display strings ("LH - Lufthansa
// Cargo", "Delhi - (DEL)") for the UI, but the itinerary sent to the API must
// always carry just the code ("LH", "DEL").
const carrierCodeOnly = (str) => {
  if (!str) return "";
  return str.split(" - ")[0].trim();
};
const airportCodeOnly = (str) => {
  if (!str) return "";
  const match = /\(([^)]+)\)/.exec(str);
  return match ? match[1] : str;
};
// LAT/TOA are optional — sending "" (the empty-input default) instead of a
// number/null makes the backend's Mongoose schema reject the whole save with
// a CastError, so always normalize to a number or null before it ever reaches
// the payload.
const numberOrNull = (v) => (v === "" || v == null ? null : Number(v));
const toItineraryPayload = (segs, lat, toa) =>
  segs.map((s) => ({
    ...s,
    carrier: carrierCodeOnly(s.carrier),
    origin: airportCodeOnly(s.origin),
    destination: airportCodeOnly(s.destination),
    lat: numberOrNull(lat),
    toa: numberOrNull(toa),
  }));
const computeChargeItem = (item, weight) => {
  const rate = Number(item.unitRate) || 0;
  const isPerKg = item.unit === "perkg";
  const out = { charge: item.charge, unit: item.unit, unitRate: rate };
  if (isPerKg) {
    out.chargeableWeight = Number(weight) || 0;
    out.totalAmount = rate * out.chargeableWeight;
  } else {
    out.totalAmount = rate;
  }
  return out;
};
const computeSection = (items, weight) => {
  const computedItems = (items || []).map((item) =>
    computeChargeItem(item, weight),
  );
  return {
    totalAmount: computedItems.reduce((sum, item) => sum + item.totalAmount, 0),
    items: computedItems,
  };
};
const buildPayload = (
  quoteExpiry,
  airlines,
  queryId,
  weight,
  originAirport,
  destAirport,
  airLineList,
) => ({
  queryId,
  quoteExpiry,
  airlines: airlines.map((airline) => {
    // Charges (and therefore the grand total) are priced once per airline —
    // every flight option under this airline shares the exact same charges.
    const origin = computeSection(
      airline.odCharges.filter((r) => r.type === "origin"),
      weight,
    );
    const destination = computeSection(
      airline.odCharges.filter((r) => r.type === "destination"),
      weight,
    );
    const freightAndSurcharges = computeSection(airline.freightCharges, weight);
    const grandTotal =
      origin.totalAmount +
      destination.totalAmount +
      freightAndSurcharges.totalAmount;
    // Fall back to a fresh lookup by carrier code — airline.carrierName can be
    // stale/blank (e.g. it was set before the airline list finished loading),
    // so never submit an empty name if the loaded list can resolve one.
    const carrierName =
      airline.carrierName ||
      airLineList?.find((a) => a.code === airline.carrierCode)?.name ||
      "";
    return {
      carrierCode: airline.carrierCode,
      carrierName,
      // Priced once per airline, like the charges — not per flight option.
      // 1 = Space Available, 2 = Subject to Availability, straight from the
      // airline's Space Availability dropdown.
      space_availability: Number(airline.spaceAvailability),
      flightOptions: airline.flightOptions.map((option) => {
        const segs = withLockedEnds(
          option.segments,
          originAirport,
          destAirport,
        );
        const departureDateAndTime = segs[0]?.departure || null;
        const arrivalDateAndTime = segs[segs.length - 1]?.arrival || null;
        return {
          departureDateAndTime,
          arrivalDateAndTime,
          transitDuration: minutesBetween(
            departureDateAndTime,
            arrivalDateAndTime,
          ),
          serviceType: airline.serviceType,
          charges: { origin, destination, freightAndSurcharges },
          itinerary: toItineraryPayload(segs, option.lat, option.toa),
          grandTotal,
          LAT: numberOrNull(option.lat),
          TOA: numberOrNull(option.toa),
        };
      }),
    };
  }),
});

const validateAirlines = (airlines, originAirport, destAirport, chargeList) => {
  const airFreightChargeIds = getAirFreightChargeIds(chargeList);
  const codes = airlines.map((a) => a.carrierCode).filter(Boolean);
  if (airlines.some((a) => !a.carrierCode))
    return "Select a carrier for every airline";
  // if (new Set(codes).size !== codes.length)
  //   return "Each airline in this quote must be different2";
  for (const airline of airlines) {
    if (!airline.spaceAvailability)
      return "Select space availability for every airline";
    // Check against the locked-ends view — the first segment's origin and the
    // last segment's destination are display-only (always shown as
    // originAirport/destAirport) and never actually written into the raw
    // segment, so checking the raw segments here would always fail.
    if (
      !airline.flightOptions?.length ||
      airline.flightOptions.some(
        (opt) =>
          !isItineraryComplete(
            withLockedEnds(opt.segments, originAirport, destAirport),
          ),
      )
    )
      return "Complete the itinerary (carrier, schedule, flight no.) for every flight option";
    if (!airline.freightCharges.some((r) => r.charge && Number(r.unitRate) > 0))
      return "Add at least one freight charge with a rate for every airline";
    if (
      airline.odCharges.some((r) => r.charge && !(Number(r.unitRate) > 0)) ||
      airline.freightCharges.some((r) => r.charge && !(Number(r.unitRate) > 0))
    )
      return "Charge rates must be greater than 0";
    // The reverse gap: picking a charge that gets blocked (e.g. a duplicate
    // Air Freight charge) leaves the row's charge empty, but the Rate input
    // itself is never blocked — so a rate can still get typed into a
    // charge-less row and silently ride along into the submitted totals.
    if (
      airline.odCharges.some((r) => !r.charge && Number(r.unitRate) > 0) ||
      airline.freightCharges.some((r) => !r.charge && Number(r.unitRate) > 0)
    )
      return "Select a charge for every rate you've entered";
    if (
      !airline.freightCharges.some((r) => airFreightChargeIds.has(r.charge))
    )
      return "Select at least Air Freight charge under Freight & Surcharges for every airline";
    if (airline.odCharges.some((r) => airFreightChargeIds.has(r.charge)))
      return AIR_FREIGHT_BLOCK_MESSAGE;
    if (
      airline.freightCharges.filter((r) => airFreightChargeIds.has(r.charge))
        .length > 1
    )
      return AIR_FREIGHT_DUPLICATE_MESSAGE;
    const seenOrigin = new Set();
    const seenDest = new Set();
    for (const row of airline.odCharges) {
      if (!row.charge) continue;
      const seen = row.type === "destination" ? seenDest : seenOrigin;
      if (seen.has(row.charge))
        return "The same charge cannot be added twice within one section";
      seen.add(row.charge);
    }
    const seenFreight = new Set();
    for (const row of airline.freightCharges) {
      if (!row.charge) continue;
      if (seenFreight.has(row.charge))
        return "The same charge cannot be added twice within one section";
      seenFreight.add(row.charge);
    }
  }
  return "";
};

// Converts a previously-saved quote (airlines[].flightOptions[] shape) back
// into this page's shape — charges are read off the first flight option
// (they're identical across all of an airline's options, by construction),
// while every flight option's own itinerary is preserved.
const mapDetailsToAirlines = (manualQuoteDetails) =>
  (manualQuoteDetails?.airlines || []).map((airline) => {
    const options = airline.flightOptions?.length
      ? airline.flightOptions
      : [{}];
    const primary = options[0] || {};
    return {
      carrierCode: airline.carrierCode || "",
      carrierName: airline.carrierName || "",
      serviceType: primary.serviceType || SERVICE_TYPES[0].value,
      spaceAvailability:
        airline.space_availability || SPACE_AVAILABILITY_OPTIONS[0].value,
      odCharges: [
        ...(primary.charges?.origin?.items || []).map((i) => ({
          type: "origin",
          charge: i.charge,
          unit: i.unit,
          unitRate: i.unitRate,
        })),
        ...(primary.charges?.destination?.items || []).map((i) => ({
          type: "destination",
          charge: i.charge,
          unit: i.unit,
          unitRate: i.unitRate,
        })),
      ],
      freightCharges: (primary.charges?.freightAndSurcharges?.items || []).map(
        (i) => ({
          charge: i.charge,
          unit: i.unit,
          unitRate: i.unitRate,
        }),
      ),
      flightOptions: options.map((option) => ({
        // The API persists LAT/TOA on the flight option itself (uppercase
        // LAT/TOA keys). Fall back to the older per-leg lat/toa for quotes
        // saved before that move.
        lat: option.LAT ?? option.itinerary?.[0]?.lat ?? "",
        toa: option.TOA ?? option.itinerary?.[0]?.toa ?? "",
        segments: option.itinerary?.length
          ? option.itinerary.map((s) => ({ ...s }))
          : [makeSegment()],
      })),
    };
  });

// ── Shared charge table — used for both Origin/Destination (withType) and
// Freight & surcharges (no type column) ────────────────────────────────────
const ChargeTable = ({
  title,
  icon,
  withType,
  rows,
  weight,
  chargeList,
  onAddRow,
  onChangeRow,
  onRemoveRow,
  copySources,
  onCopy,
  subtotals,
  addLabel,
  forbiddenCharges,
  forbiddenMessage,
  singletonCharges,
  singletonMessage,
  readOnly,
  showErrors,
}) => {
  const [blockedMessage, setBlockedMessage] = useState("");
  // A blocked selection (e.g. a duplicate Air Freight charge) leaves `rows`
  // unchanged, so the message stays up as feedback on that attempt. Once the
  // rows actually change — e.g. the user removes the conflicting row — the
  // reason for the message may no longer hold, so clear it rather than
  // leaving stale text on screen.
  useEffect(() => {
    setBlockedMessage("");
  }, [rows]);
  const gridCols = withType
    ? "130px minmax(220px,1fr) 140px 120px 110px 130px 36px"
    : "minmax(220px,1fr) 140px 120px 110px 130px 36px";
  const gridGap = 22;
  const headers = withType
    ? ["Type", "Charge", "Unit", "Rate", "Chg. weight", "Total", ""]
    : ["Charge", "Unit", "Rate", "Chg. weight", "Total", ""];

  return (
    <div
      style={{
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: 14,
        overflow: "hidden",
        boxShadow:
          "0 1px 3px rgba(16,24,40,0.04), 0 1px 2px rgba(16,24,40,0.03)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          padding: "16px 20px",
          background: "#FFFCF2",
          borderBottom: `1px solid ${ROW_BORDER}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: "#FFF3D2",
              border: `1px solid ${TINT_BORDER}`,
              color: ACCENT_DARK,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
            }}
          >
            {icon}
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#20242B" }}>
            {title}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          {!readOnly && <CopyFromMenu sources={copySources} onCopy={onCopy} />}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {subtotals.map((s, i) => (
              <span
                key={i}
                style={{
                  fontSize: 12,
                  color: ACCENT_DARK,
                  background: "#FFF8E7",
                  border: `1px solid ${TINT_BORDER}`,
                  borderRadius: 999,
                  padding: "5px 12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {s.label}{" "}
                <strong style={{ color: "#1f2937" }}>{s.value}</strong>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          padding: "18px 20px",
          overflowX: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {rows.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              gap: gridGap,
              minWidth: withType ? 1000 : 850,
              paddingBottom: 10,
              marginBottom: 10,
              borderBottom: `1px solid ${ROW_BORDER}`,
            }}
          >
            {headers.map((h, i) => (
              <span
                key={i}
                className="text-[10.5px] uppercase tracking-wide font-semibold text-gray-400"
              >
                {h}
                {(h === "Charge" || h === "Rate") && (
                  <span className="text-red-500"> *</span>
                )}
              </span>
            ))}
          </div>
        )}

        {rows.map((row, index) => {
          // A row is incomplete once it's half-filled — a charge picked with
          // no rate, or a rate typed with no charge — but a still-blank extra
          // row (neither field touched) isn't flagged.
          const incomplete =
            showErrors && !!row.charge !== Number(row.unitRate) > 0;
          const chargeMissing = incomplete && !row.charge;
          const rateMissing = incomplete && !(Number(row.unitRate) > 0);
          return (
            <div
              key={index}
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: gridGap,
                minWidth: withType ? 1000 : 850,
                alignItems: "center",
                paddingBottom: 14,
                marginBottom: 14,
                borderBottom:
                  index < rows.length - 1 ? `1px solid #F4F1EA` : "none",
              }}
            >
              {withType && (
                <SelectItem
                  options={ORIGIN_DESTINATION_CHARGE_TYPE}
                  width="100%"
                  name={`odType-${index}`}
                  value={row.type}
                  disabled={readOnly}
                  handleChange={() => {}}
                  setFieldValue={(_e, value) =>
                    onChangeRow(index, "type", value)
                  }
                />
              )}
              <div style={errorOutline(chargeMissing)}>
                <SelectMultipleWithSearch
                  options={chargeList}
                  width="100%"
                  name={`charge-${index}`}
                  placeholder="Select charge"
                  value={row.charge || undefined}
                  disabled={readOnly}
                  handleChange={() => {}}
                  setFieldValue={(_name, value) => {
                    if (forbiddenCharges?.includes(value)) {
                      setBlockedMessage(
                        forbiddenMessage ||
                          "This charge cannot be selected here.",
                      );
                      return;
                    }
                    if (
                      singletonCharges?.includes(value) &&
                      rows.some(
                        (r, i) =>
                          i !== index && singletonCharges.includes(r.charge),
                      )
                    ) {
                      setBlockedMessage(
                        singletonMessage ||
                          "This charge is already selected in another row.",
                      );
                      return;
                    }
                    setBlockedMessage("");
                    onChangeRow(index, "charge", value);
                  }}
                />
              </div>
              <SelectItem
                options={AIR_CHARGE_UNIT_TYPE}
                width="100%"
                name={`unit-${index}`}
                value={row.unit}
                disabled={readOnly}
                handleChange={() => {}}
                setFieldValue={(_e, value) => onChangeRow(index, "unit", value)}
              />
              <input
                type="text"
                placeholder="0.00"
                className={INPUT_CLS}
                style={{ textAlign: "right", ...errorOutline(rateMissing) }}
                value={row.unitRate}
                disabled={readOnly}
                onChange={(e) => onChangeRow(index, "unitRate", e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <div
                className={READONLY_CLS}
                style={{ justifyContent: "flex-end" }}
              >
                {weight ?? 0} kg
              </div>
              <div
                className={READONLY_CLS}
                style={{
                  fontWeight: 600,
                  color: "#1f2937",
                  justifyContent: "flex-end",
                }}
              >
                {money(rowAmount(row, weight))}
              </div>
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() => onRemoveRow(index)}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: "#fef2f2",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fca5a5",
                  }}
                  title="Remove"
                >
                  <Trash2 size={13} />
                </button>
              ) : (
                <div />
              )}
            </div>
          );
        })}

        {blockedMessage && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              color: "#B91C1C",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 12.5,
              fontWeight: 500,
            }}
          >
            {blockedMessage}
          </div>
        )}

        {!readOnly && (
          <button
            type="button"
            onClick={onAddRow}
            style={{
              alignSelf: "flex-start",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12.5,
              fontWeight: 600,
              color: ACCENT_DARK,
              background: "#FFFBEE",
              border: `1px dashed ${ACCENT}`,
              borderRadius: 999,
              cursor: "pointer",
              padding: "7px 14px",
              marginTop: 2,
            }}
          >
            <Plus size={14} />
            {addLabel}
          </button>
        )}
      </div>
    </div>
  );
};

// ── Itinerary segment card ──────────────────────────────────────────────
const ERROR_COLOR = "#ef4444";
const errorOutline = (isInvalid) =>
  isInvalid
    ? {
        outline: `1.5px solid ${ERROR_COLOR}`,
        outlineOffset: 2,
        borderRadius: 8,
      }
    : undefined;
const errorLabelStyle = (isInvalid) =>
  isInvalid ? { color: ERROR_COLOR } : undefined;

const SegmentCard = ({
  seg,
  index,
  isFirst,
  isLast,
  canRemove,
  airLineList,
  airPort,
  originAirport,
  destAirport,
  layoverLabel,
  onUpdate,
  onRemove,
  readOnly,
  showErrors,
}) => {
  const missing = {
    carrier: showErrors && !seg.carrier,
    flight: showErrors && !seg.flight,
    origin: showErrors && !isFirst && !seg.origin,
    destination: showErrors && !isLast && !seg.destination,
    departure: showErrors && !seg.departure,
    arrival: showErrors && !seg.arrival,
  };

  return (
    <div
      style={{
        border: `1px solid ${TINT_BORDER}`,
        background: "#FFFDF5",
        borderRadius: 12,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: ACCENT,
              color: "#fff",
              fontSize: 11,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {index + 1}
          </div>
          <div className="text-[12px] uppercase tracking-wide font-bold text-gray-400">
            Segment
          </div>
        </div>
        {canRemove && !readOnly && (
          <div
            onClick={onRemove}
            style={{
              cursor: "pointer",
              fontSize: 12.5,
              color: "#9A9384",
              fontWeight: 500,
            }}
          >
            Remove
          </div>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
        }}
      >
        <div>
          <label className={LABEL_CLS} style={errorLabelStyle(missing.carrier)}>
            Carrier <span className="text-red-500">*</span>
          </label>
          <div style={errorOutline(missing.carrier)}>
            <SelectMultipleWithSearch
              options={airLineList}
              width="100%"
              name={`seg-carrier-${index}`}
              value={seg.carrier || undefined}
              disabled={readOnly}
              handleChange={() => {}}
              setFieldValue={(_n, val) => onUpdate("carrier", val)}
            />
          </div>
        </div>
        <div>
          <label className={LABEL_CLS} style={errorLabelStyle(missing.flight)}>
            Flight no. <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={seg.flight || ""}
            placeholder="AI-101"
            className={INPUT_CLS}
            disabled={readOnly}
            style={errorOutline(missing.flight)}
            onChange={(e) => onUpdate("flight", e.target.value)}
          />
        </div>
        <div>
          <div className="flex items-center gap-1 mb-1">
            <label
              className="text-[10.5px] uppercase tracking-wide font-semibold text-gray-400"
              style={errorLabelStyle(missing.origin)}
            >
              Origin
            </label>
            {!isFirst && <span className="text-red-500">*</span>}
            {isFirst && <Lock size={11} style={{ color: ACCENT }} />}
          </div>
          {isFirst ? (
            <input
              readOnly
              value={originAirport || seg.origin || ""}
              className={READONLY_CLS + " border-amber-200 bg-amber-50 w-full"}
            />
          ) : (
            <div style={errorOutline(missing.origin)}>
              <SelectMultipleWithSearch
                options={airPort}
                width="100%"
                name={`seg-origin-${index}`}
                value={seg.origin || undefined}
                disabled={readOnly}
                handleChange={() => {}}
                setFieldValue={(_n, val) => onUpdate("origin", val)}
              />
            </div>
          )}
        </div>
        <div>
          <div className="flex items-center gap-1 mb-1">
            <label
              className="text-[10.5px] uppercase tracking-wide font-semibold text-gray-400"
              style={errorLabelStyle(missing.destination)}
            >
              Destination
            </label>
            {!isLast && <span className="text-red-500">*</span>}
            {isLast && <Lock size={11} style={{ color: ACCENT }} />}
          </div>
          {isLast ? (
            <input
              readOnly
              value={destAirport || seg.destination || ""}
              className={READONLY_CLS + " border-amber-200 bg-amber-50 w-full"}
            />
          ) : (
            <div style={errorOutline(missing.destination)}>
              <SelectMultipleWithSearch
                options={airPort}
                width="100%"
                name={`seg-dest-${index}`}
                value={seg.destination || undefined}
                disabled={readOnly}
                handleChange={() => {}}
                setFieldValue={(_n, val) => onUpdate("destination", val)}
              />
            </div>
          )}
        </div>
        <div>
          <label className={LABEL_CLS}>Aircraft</label>
          <input
            type="text"
            value={seg.aircraft || ""}
            placeholder="B787, A350"
            className={INPUT_CLS}
            disabled={readOnly}
            onChange={(e) => onUpdate("aircraft", e.target.value)}
          />
        </div>
        <div>
          <label
            className={LABEL_CLS}
            style={errorLabelStyle(missing.departure)}
          >
            Departure <span className="text-red-500">*</span>
          </label>
          <DateTimePicker
            value={seg.departure ? dayjs(seg.departure) : null}
            format="YYYY-MM-DD HH:mm"
            disablePast
            minDateTime={dayjs()}
            ampm={false}
            disabled={readOnly}
            minDateTime={dayjs().startOf("day")}
            maxDateTime={dayjs().add(3, "month").endOf("day")}
            onChange={(date) =>
              onUpdate("departure", date ? date.toISOString() : null)
            }
            slotProps={{
              textField: {
                size: "small",
                sx: DATE_PICKER_SX,
                error: missing.departure,
              },
            }}
          />
        </div>
        <div>
          <label className={LABEL_CLS} style={errorLabelStyle(missing.arrival)}>
            Arrival <span className="text-red-500">*</span>
          </label>
          <DateTimePicker
            value={seg.arrival ? dayjs(seg.arrival) : null}
            format="YYYY-MM-DD HH:mm"
            disablePast
            minDateTime={dayjs()}
            ampm={false}
            disabled={readOnly}
            minDateTime={
              seg.departure ? dayjs(seg.departure).add(1, "minute") : undefined
            }
            maxDateTime={dayjs().add(3, "month").endOf("day")}
            onChange={(date) =>
              onUpdate("arrival", date ? date.toISOString() : null)
            }
            slotProps={{
              textField: {
                size: "small",
                sx: DATE_PICKER_SX,
                error: missing.arrival,
              },
            }}
          />
        </div>
        <div>
          <label className={LABEL_CLS}>Leg duration</label>
          <div className={READONLY_CLS} style={{ fontFamily: "monospace" }}>
            {durationLabel(minutesBetween(seg.departure, seg.arrival))}
          </div>
        </div>
        <div>
          <label className={LABEL_CLS}>Operation Type</label>
          <select
            value={seg.operationType  || ""}
            onChange={(e) => onUpdate("operationType", e.target.value)}
            disabled={readOnly}
            className={INPUT_CLS}
          >
            <option value="">Select Type</option>
            <option value="FRT">FRT</option>
            <option value="PAX">PAX</option>
            <option value="TRK">TRK</option>
          </select>
        </div>
      </div>

      {layoverLabel && (
        <div>
          <span
            style={{
              background: "#FFF6E2",
              border: `1px solid ${TINT_BORDER}`,
              borderRadius: 999,
              padding: "4px 11px",
              fontSize: 12,
              fontWeight: 600,
              color: "#8C6A12",
            }}
          >
            {layoverLabel}
          </span>
        </div>
      )}
    </div>
  );
};

// Small logo badge shared by the airline tabs and the summary table — falls
// back to hiding (tabs) or the code text (summary table) if the airline has
// no logo on file, or the logo URL 404s.
const AirlineLogoDot = ({ logo, size = 18 }) => {
  const [failed, setFailed] = useState(false);
  if (!logo || failed) return null;
  return (
    <img
      src={logo}
      alt=""
      onError={() => setFailed(true)}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
        borderRadius: 4,
        background: "#fff",
        flex: "none",
      }}
    />
  );
};

const AirlineBadge = ({ code, logo, size = 34, active }) => {
  const [failed, setFailed] = useState(false);
  const showLogo = logo && !failed;
  return (
    <div
      style={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: 9,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        background: showLogo ? "#fff" : active ? ACCENT : "#F4F1EA",
        border: showLogo ? `1px solid ${CARD_BORDER}` : "none",
        fontFamily: "monospace",
        fontSize: 12.5,
        fontWeight: 600,
        color: active ? "#fff" : "#6E685C",
      }}
    >
      {showLogo ? (
        <img
          src={logo}
          alt={code || ""}
          onError={() => setFailed(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            padding: 3,
          }}
        />
      ) : (
        code || "—"
      )}
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────────
const ManualQuoteHtmlUI = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { queryDetails, manualQuoteDetails, viewOnly } = useSelector(
    (state) => state.manualQuoteSlice,
  );
  const readOnly = !!viewOnly;

  const [chargeList, setChargeList] = useState([]);
  const [airLineList, setAirLineList] = useState([]);
  const [airPort, setAirPort] = useState([]);
  // Recomputed from the live charge master (see getAirFreightChargeIds) so a
  // newly added "Air Freight"-named charge is caught without a code change.
  const airFreightChargeIds = Array.from(getAirFreightChargeIds(chargeList));

  const [quoteExpiry, setQuoteExpiry] = useState(null);
  const [airlines, setAirlines] = useState([makeAirline()]);
  const [activeIndex, setActiveIndex] = useState(0);
  // Which flight option (schedule/itinerary) of the active airline is shown —
  // resets to 0 whenever the active airline changes (selectAirline/addAirline/
  // removeAirline), since option indices aren't comparable across airlines.
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  // Set once a submit has been attempted — turns on red field-level
  // highlighting for whichever itinerary fields are still missing, so the
  // generic "complete the itinerary" error can be traced to the exact spot.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [savedState, setSavedState] = useState(null); // null | { mode: "one"|"all", index? }

  // Quotation history (view-only mode) — the list of past snapshots for this
  // quote, and which one (if any other than the latest) is currently loaded
  // into the form below.
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewedVersion, setViewedVersion] = useState(null); // null = latest

  const totalWeight =
    queryDetails?.packagingDetails?.packagingType?.totalweight;
  const originAirport = queryDetails?.originPort?.originAirport || "";
  const destAirport = queryDetails?.destinationPort?.destinationAirport || "";
  const isEdit = Object.keys(manualQuoteDetails || {}).length > 0;

  const loadQuoteIntoForm = (doc) => {
    setQuoteExpiry(doc?.quoteExpiry || null);
    setAirlines(
      doc?.airlines?.length ? mapDetailsToAirlines(doc) : [makeAirline()],
    );
    setActiveIndex(0);
    setActiveOptionIndex(0);
  };

  useEffect(() => {
    dispatch(getAllOriginDestinationCharges()).then((res) => {
      setChargeList(
        res?.payload?.map((item) => ({
          label: item?.charge_name,
          value: item?._id,
        })),
      );
    });
    // getAirlinesListByCode (/airlines_list) reliably has a name for every
    // carrier code; getAirlinesList (/markup/listAirlines) is missing names
    // for many codes but carries logos — merge both so the carrier dropdown
    // always shows "CODE - Name" while tabs/summary still get a logo where
    // one exists.
    Promise.all([
      dispatch(getAirlinesListByCode()),
      dispatch(getAirlinesList()),
    ]).then(([byCodeRes, byLogoRes]) => {
      const logoByCode = {};
      (byLogoRes?.payload || []).forEach((item) => {
        if (item.code) logoByCode[item.code] = item.logo;
      });
      setAirLineList(
        (byCodeRes?.payload || []).map((item) => ({
          // Some airlines share a carr_code across records (e.g. a cargo
          // division vs. the passenger carrier) — key the option on both
          // code and name so each stays independently selectable instead
          // of the Select highlighting every option with a matching code.
          value: item.carr_name
            ? `${item.carr_code} - ${item.carr_name}`
            : item.carr_code,
          label: item.carr_name
            ? `${item.carr_code} - ${item.carr_name}`
            : item.carr_code,
          code: item.carr_code,
          name: item.carr_name,
          logo: logoByCode[item.carr_code],
        })),
      );
    });
    dispatch(getallairports()).then((res) => {
      setAirPort(
        res?.payload?.data?.data?.map((item) => ({
          value: `${item.cityName} - (${item.code})`,
          label: `${item.cityName} - (${item.code})`,
        })),
      );
    });
  }, []);

  // Prefill from a previously-saved quote (edit or view flow)
  useEffect(() => {
    if (manualQuoteDetails?.airlines?.length) {
      loadQuoteIntoForm(manualQuoteDetails);
    }
  }, [manualQuoteDetails]);

  // View-only mode: load this quote's version history so KAM can inspect and
  // open past snapshots (see getManualQuoteHistory — GET
  // /manual-quote/get-manual-quote-history/:manualQuoteId, newest-first).
  useEffect(() => {
    if (!readOnly || !manualQuoteDetails?._id) return;
    setHistoryLoading(true);
    dispatch(getManualQuoteHistory(manualQuoteDetails._id))
      .then((res) => setHistoryEntries(res?.payload?.nodeData || []))
      .finally(() => setHistoryLoading(false));
  }, [readOnly, manualQuoteDetails?._id]);

  const handleViewHistoryState = (entry) => {
    if (!entry?.snapshot) return;
    setViewedVersion(entry.version);
    loadQuoteIntoForm(entry.snapshot);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToLatest = () => {
    setViewedVersion(null);
    loadQuoteIntoForm(manualQuoteDetails);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const safeActiveIndex = Math.min(activeIndex, airlines.length - 1);
  const activeAirline = airlines[safeActiveIndex];
  const safeOptionIndex = Math.min(
    activeOptionIndex,
    activeAirline.flightOptions.length - 1,
  );
  const activeOption = activeAirline.flightOptions[safeOptionIndex];
  const combinedGrandTotal = airlines.reduce(
    (sum, a) => sum + airlineTotals(a, totalWeight).grand,
    0,
  );

  const carrierLabel = (code) => airLineList.find((a) => a.code === code)?.name;
  const carrierLogo = (code) => airLineList.find((a) => a.code === code)?.logo;

  const mutateAirline = (index, fn) => {
    setAirlines((prev) => {
      const clone = prev.map((a) => JSON.parse(JSON.stringify(a)));
      fn(clone[index]);
      return clone;
    });
    setSavedState(null);
    setFormError("");
  };

  // Mutates only the active airline's active flight option (its segments) —
  // charges stay on mutateAirline since they're shared across every option.
  const mutateActiveOption = (fn) => {
    mutateAirline(safeActiveIndex, (a) => fn(a.flightOptions[safeOptionIndex]));
  };

  const selectAirline = (index) => {
    setActiveIndex(index);
    setActiveOptionIndex(0);
  };

  const addFlightOption = () => {
    const newIndex = activeAirline.flightOptions.length;
    // A brand-new option always starts as a single segment, so both its ends
    // get overridden by the origin/destination lock (withLockedEnds) anyway —
    // no need to guess a starting origin here. LAT/TOA are usually the same
    // cargo acceptance/handling window across an airline's schedules, so
    // carry the currently active option's values forward instead of leaving
    // the new option blank — still editable per option if it ever differs.
    const { lat, toa } = activeOption;
    mutateAirline(safeActiveIndex, (a) => {
      const opt = makeFlightOption(a.carrierCode);
      opt.lat = lat;
      opt.toa = toa;
      a.flightOptions.push(opt);
    });
    setActiveOptionIndex(newIndex);
  };

  const removeFlightOption = (index) => {
    mutateAirline(safeActiveIndex, (a) => {
      if (a.flightOptions.length > 1) a.flightOptions.splice(index, 1);
    });
    setActiveOptionIndex((prev) => {
      if (index === prev) return Math.max(0, index - 1);
      if (index < prev) return prev - 1;
      return prev;
    });
  };

  const otherAirlineSources = (excludeIndex) =>
    airlines
      .map((al, i) =>
        i === excludeIndex
          ? null
          : {
              key: String(i),
              label:
                carrierLabel(al.carrierCode) ||
                al.carrierCode ||
                `New airline ${i + 1}`,
              airline: al,
            },
      )
      .filter(Boolean);

  const isDevAutofillTrigger =
    DEV_AUTOFILL_ENABLED &&
    String(activeAirline.odCharges?.[0]?.unitRate) === DEV_AUTOFILL_TRIGGER;

  const handleDevAutofill = (e) => {
    e.preventDefault();
    const fallbackAirline = airLineList?.[0];
    const preferredAirline = airLineList.find((a) => a.code === "LH");
    const chosenCode =
      preferredAirline?.code ||
      activeAirline.carrierCode ||
      fallbackAirline?.code ||
      "";
    const chosenName =
      airLineList.find((a) => a.code === chosenCode)?.name ||
      fallbackAirline?.name ||
      "";
    const departure1 = dayjs()
      .add(2, "day")
      .hour(10)
      .minute(0)
      .second(0)
      .toISOString();
    const arrival1 = dayjs()
      .add(3, "day")
      .hour(14)
      .minute(0)
      .second(0)
      .toISOString();
    const departure2 = dayjs()
      .add(4, "day")
      .hour(18)
      .minute(0)
      .second(0)
      .toISOString();
    const arrival2 = dayjs()
      .add(5, "day")
      .hour(22)
      .minute(0)
      .second(0)
      .toISOString();
    const chargeA = chargeList?.[0]?.value || "";
    const chargeB = chargeList?.[1]?.value || chargeA;
    const chargeC = chargeList?.[2]?.value || chargeA;

    if (!quoteExpiry) setQuoteExpiry(dayjs().add(30, "day").toISOString());

    mutateAirline(safeActiveIndex, (a) => {
      a.carrierCode = chosenCode;
      a.carrierName = chosenName;
      a.serviceType = "Express Cargo";
      a.odCharges = [
        { type: "origin", charge: chargeA, unit: "perkg", unitRate: 45 },
        {
          type: "destination",
          charge: chargeB,
          unit: "lumpsum",
          unitRate: 2500,
        },
      ];
      a.freightCharges = [{ charge: chargeC, unit: "perkg", unitRate: 120 }];
      // At least 2 flight options — same shared charges above, different schedules.
      a.flightOptions = [
        {
          segments: [
            {
              carrier: chosenCode,
              operationType: "FRT",
              origin: originAirport,
              destination: destAirport,
              departure: departure1,
              arrival: arrival1,
              aircraft: "B787-9",
              flight: `${chosenCode}101`,
            },
          ],
        },
        {
          segments: [
            {
              carrier: chosenCode,
              operationType: "PAX",
              origin: originAirport,
              destination: destAirport,
              departure: departure2,
              arrival: arrival2,
              aircraft: "A350",
              flight: `${chosenCode}202`,
            },
          ],
        },
      ];
    });
    setActiveOptionIndex(0);
  };

  const addAirline = () => {
    setActiveIndex(airlines.length);
    setActiveOptionIndex(0);
    setAirlines((prev) => [...prev, makeAirline()]);
    setSavedState(null);
    setFormError("");
  };

  const removeAirline = (index) => {
    setAirlines((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index),
    );
    setActiveIndex((prev) => {
      if (index === prev) return Math.max(0, index - 1);
      if (index < prev) return prev - 1;
      return prev;
    });
    setActiveOptionIndex(0);
    setSavedState(null);
  };

  const handleSaveActiveAirline = () => {
    if (!activeAirline.carrierCode) {
      setFormError("Select a carrier before saving this airline");
      return;
    }
    setFormError("");
    setSavedState({ mode: "one", index: safeActiveIndex });
  };

  const handleSubmit = () => {
    setSubmitAttempted(true);
    if (!quoteExpiry) {
      setFormError("Expiry date is required");
      return;
    }
    const err = validateAirlines(
      airlines,
      originAirport,
      destAirport,
      chargeList,
    );
    if (err) {
      setFormError(err);
      return;
    }
    setFormError("");

    const payload = buildPayload(
      quoteExpiry,
      airlines,
      queryDetails?._id,
      totalWeight,
      originAirport,
      destAirport,
      airLineList,
    );
    setLoading(true);

    const handleResult = (result) => {
      setLoading(false);
      const statusCode = result?.payload?.statusCode;
      const message =
        result?.payload?.message ||
        (typeof result?.payload === "string"
          ? result.payload
          : "Something went wrong");
      const isSuccess = [200, 201].includes(statusCode);
      // antd's notification.useNotification() API is keyed by type
      // ("success"/"error"/...) — see src/shared/notification/Notification.jsx.
      dispatch(
        updatenotificationdata({
          message,
          success: isSuccess ? "success" : "error",
        }),
      );
      if (isSuccess) {
        setSavedState({ mode: "all" });
        navigate("/QueriesKAM");
        dispatch(clearManualQuote());
      }
    };

    if (isEdit) {
      payload.manualqueryId = manualQuoteDetails?._id;
      dispatch(editManualQuote(payload)).then(handleResult);
    } else {
      dispatch(addManualQuote(payload)).then(handleResult);
    }
  };

  const savedMsg =
    savedState?.mode === "all"
      ? `Submitted rates for all ${airlines.length} airline${airlines.length > 1 ? "s" : ""} · combined total ${money(combinedGrandTotal)}`
      : savedState?.mode === "one"
        ? `Saved ${carrierLabel(airlines[savedState.index]?.carrierCode) || "this airline"} locally · grand total ${money(
            airlineTotals(airlines[savedState.index], totalWeight).grand,
          )}. Click "${airlines.length > 1 ? `Submit all ${airlines.length} airlines` : "Submit rates"}" to send the full quote.`
        : "";

  const finalSegments = withLockedEnds(
    activeOption.segments,
    originAirport,
    destAirport,
  );
  const transitMinutes = finalSegments.length
    ? minutesBetween(
        finalSegments[0].departure,
        finalSegments[finalSegments.length - 1].arrival,
      )
    : 0;

  // Same rules validateAirlines enforces on submit, checked reactively so the
  // submit button is disabled the moment they're violated — not just after
  // the user clicks and gets bounced with an error. Covers two cases: more
  // than one Air Freight charge selected, and a charge that got blocked from
  // selection (duplicate Air Freight, or a forbidden charge) leaving the
  // row's charge blank while its unblocked Rate input still has a value —
  // which would otherwise ride along into the submitted totals as
  // charge: "".
  const hasFreightRowIssue = (rows) =>
    rows.filter((r) => airFreightChargeIds.includes(r.charge)).length > 1 ||
    rows.some((r) => !r.charge && Number(r.unitRate) > 0);
  const hasBlockingChargeRowIssue = airlines.some(
    (a) =>
      hasFreightRowIssue(a.freightCharges) ||
      a.odCharges.some((r) => !r.charge && Number(r.unitRate) > 0),
  );

  const submitLabel = loading
    ? "Processing..."
    : isEdit
      ? "Update rates"
      : airlines.length > 1
        ? `Submit all ${airlines.length} airlines`
        : "Submit rates";

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MainContainer>
        <CustomeHeader
          title={readOnly ? "View Manual Quotation" : ""}
          description={
            readOnly ? "Read-only — this quotation cannot be edited here." : ""
          }
          type={readOnly ? "cancel" : undefined}
        />
        <QueryDetailHeader queryDetails={queryDetails} />

        <div
          className="mt-5 rounded-2xl overflow-hidden"
          style={{
            background: "#FFFDF5",
            border: `1px solid ${CARD_BORDER}`,
            boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
          }}
        >
          <div
            className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b"
            style={{ borderColor: ROW_BORDER }}
          >
            <h2 className="text-base font-bold text-gray-800">
              Manual quotation
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 font-medium">
                Expiry Date And Time
              </span>
              <DateTimePicker
                value={quoteExpiry ? dayjs(quoteExpiry) : null}
                minDateTime={dayjs()}
                format="YYYY-MM-DD HH:mm"
                ampm={false}
                disabled={readOnly}
                disablePast
                minDateTime={dayjs().startOf("day")}
                maxDateTime={dayjs().add(3, "month").endOf("day")}
                onChange={(date) => {
                  setQuoteExpiry(date ? date.toISOString() : null);
                  setSavedState(null);
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: "Select date & time",
                    sx: { ...DATE_PICKER_SX, minWidth: 190, width: "auto" },
                  },
                }}
              />
            </div>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Airline tabs */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {airlines.map((al, i) => {
                const active = i === safeActiveIndex;
                const label =
                  carrierLabel(al.carrierCode) ||
                  al.carrierCode ||
                  `New airline ${i + 1}`;
                const incomplete =
                  submitAttempted &&
                  al.flightOptions.some(
                    (opt) =>
                      !isItineraryComplete(
                        withLockedEnds(
                          opt.segments,
                          originAirport,
                          destAirport,
                        ),
                      ),
                  );
                return (
                  <div
                    key={i}
                    onClick={() => selectAirline(i)}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      borderRadius: 999,
                      padding: "8px 10px 8px 14px",
                      fontSize: 12.5,
                      fontWeight: 600,
                      border: incomplete
                        ? `1px solid ${ERROR_COLOR}`
                        : active
                          ? "1px solid #E39610"
                          : `1px solid ${CARD_BORDER}`,
                      background: active ? ACCENT : "#fff",
                      color: active ? "#20242B" : "#4A463E",
                      boxShadow: active
                        ? "0 3px 10px rgba(198,140,20,0.22)"
                        : "none",
                    }}
                  >
                    <AirlineLogoDot logo={carrierLogo(al.carrierCode)} />
                    <span>{label}</span>
                    {incomplete && (
                      <span
                        title="Itinerary incomplete"
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: ERROR_COLOR,
                          flex: "none",
                        }}
                      />
                    )}
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontWeight: 500,
                        opacity: 0.8,
                      }}
                    >
                      {money(airlineTotals(al, totalWeight).grand)}
                    </span>
                    {!readOnly && airlines.length > 1 && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          removeAirline(i);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: active ? "#20242B" : "#9A9384",
                          opacity: 0.75,
                        }}
                        title="Remove airline"
                      >
                        <X size={13} />
                      </span>
                    )}
                  </div>
                );
              })}
              {!readOnly && (
                <button
                  type="button"
                  onClick={addAirline}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    border: `1px dashed ${ACCENT}`,
                    color: ACCENT_DARK,
                    borderRadius: 999,
                    padding: "8px 14px",
                    fontSize: 12.5,
                    fontWeight: 600,
                    background: "transparent",
                  }}
                >
                  <Plus size={14} />
                  Add airline
                </button>
              )}
            </div>

            {/* Active airline */}
            <div
              style={{
                border: `1px solid ${CARD_BORDER}`,
                borderRadius: 14,
                padding: "18px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
                background: "#fff",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                  gap: 14,
                }}
              >
                <div>
                  <label className={LABEL_CLS}>
                    Carrier <span className="text-red-500">*</span>
                  </label>
                  <SelectMultipleWithSearch
                    options={airLineList}
                    width="100%"
                    name="carrierCode"
                    placeholder="Select carrier"
                    // Two airline master records can share the same carr_code
                    // (e.g. "LH - Lufthansa" and "LH - Lufthansa Cargo"), so the
                    // Select's own identity can't be the bare code — that made
                    // antd highlight every option sharing that code at once.
                    // airLineList.value is the unique "code - name" pair; derive
                    // it here from the clean carrierCode/carrierName already
                    // stored on the airline, so nothing downstream (payload,
                    // dedupe check, segment carrier) has to change.
                    value={
                      airLineList.find(
                        (a) =>
                          a.code === activeAirline.carrierCode &&
                          a.name === activeAirline.carrierName,
                      )?.value || undefined
                    }
                    disabled={readOnly}
                    handleChange={() => {}}
                    setFieldValue={(_name, value) => {
                      const match = airLineList.find((a) => a.value === value);
                      mutateAirline(safeActiveIndex, (a) => {
                        a.carrierCode = match?.code || "";
                        a.carrierName = match?.name || "";
                        a.flightOptions = a.flightOptions.map((opt) => ({
                          ...opt,
                          segments: opt.segments.map((s) =>
                            s.carrier
                              ? s
                              : { ...s, carrier: match?.code || "" },
                          ),
                        }));
                      });
                    }}
                  />
                </div>
                <div>
                  <label className={LABEL_CLS}>Service type</label>
                  <SelectItem
                    options={SERVICE_TYPES}
                    width="100%"
                    name="serviceType"
                    value={activeAirline.serviceType}
                    disabled={readOnly}
                    handleChange={() => {}}
                    setFieldValue={(_e, value) =>
                      mutateAirline(
                        safeActiveIndex,
                        (a) => (a.serviceType = value),
                      )
                    }
                  />
                </div>
                <div>
                  <label className={LABEL_CLS}>
                    Space availability <span className="text-red-500">*</span>
                  </label>
                  <SelectItem
                    options={SPACE_AVAILABILITY_OPTIONS}
                    width="100%"
                    name="spaceAvailability"
                    placeholder="Select availability"
                    value={activeAirline.spaceAvailability}
                    disabled={readOnly}
                    handleChange={() => {}}
                    setFieldValue={(_e, value) =>
                      mutateAirline(
                        safeActiveIndex,
                        (a) => (a.spaceAvailability = value),
                      )
                    }
                  />
                </div>
                <div>
                  <label className={LABEL_CLS}>Chargeable weight</label>
                  <div
                    style={{
                      border: "1px dashed #E7E1D5",
                      background: "#F8F5EF",
                      borderRadius: 9,
                      padding: "10px 12px",
                      fontSize: 13.5,
                      fontFamily: "monospace",
                      color: "#6E685C",
                    }}
                  >
                    {totalWeight ?? 0} kg
                  </div>
                </div>
              </div>

              {!readOnly && isDevAutofillTrigger && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: `1px dashed ${ACCENT}`,
                    background: "#FFFBEE",
                  }}
                >
                  <span
                    style={{
                      fontSize: 12.5,
                      color: ACCENT_DARK,
                      fontWeight: 500,
                    }}
                  >
                    Dev shortcut detected — fill carrier, expiry, charges and
                    itinerary with sample data?
                  </span>
                  <button
                    type="button"
                    onClick={handleDevAutofill}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: "#1f2937",
                      background: ACCENT,
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 12px",
                      cursor: "pointer",
                      boxShadow: "0 2px 6px rgba(245,166,35,0.35)",
                    }}
                  >
                    <Zap size={13} />
                    Autofill
                  </button>
                </div>
              )}

              <ChargeTable
                title="Origin & destination charges"
                icon={<Package size={14} />}
                withType
                rows={activeAirline.odCharges}
                weight={totalWeight}
                chargeList={chargeList}
                onAddRow={() =>
                  mutateAirline(safeActiveIndex, (a) =>
                    a.odCharges.push(makeODRow("origin")),
                  )
                }
                onChangeRow={(i, field, value) =>
                  mutateAirline(
                    safeActiveIndex,
                    (a) => (a.odCharges[i][field] = value),
                  )
                }
                onRemoveRow={(i) =>
                  mutateAirline(safeActiveIndex, (a) =>
                    a.odCharges.splice(i, 1),
                  )
                }
                copySources={otherAirlineSources(safeActiveIndex)}
                onCopy={(source) =>
                  mutateAirline(
                    safeActiveIndex,
                    (a) =>
                      (a.odCharges = JSON.parse(
                        JSON.stringify(source.airline.odCharges),
                      )),
                  )
                }
                subtotals={[
                  {
                    label: "Origin",
                    value: money(
                      odAmount(activeAirline.odCharges, "origin", totalWeight),
                    ),
                  },
                  {
                    label: "Dest",
                    value: money(
                      odAmount(
                        activeAirline.odCharges,
                        "destination",
                        totalWeight,
                      ),
                    ),
                  },
                ]}
                addLabel="Add charge"
                forbiddenCharges={airFreightChargeIds}
                forbiddenMessage={AIR_FREIGHT_BLOCK_MESSAGE}
                readOnly={readOnly}
                showErrors={submitAttempted}
              />

              <ChargeTable
                title="Freight & surcharges"
                icon={<Weight size={14} />}
                withType={false}
                rows={activeAirline.freightCharges}
                weight={totalWeight}
                chargeList={chargeList}
                onAddRow={() =>
                  mutateAirline(safeActiveIndex, (a) =>
                    a.freightCharges.push(makeFreightRow()),
                  )
                }
                onChangeRow={(i, field, value) =>
                  mutateAirline(
                    safeActiveIndex,
                    (a) => (a.freightCharges[i][field] = value),
                  )
                }
                onRemoveRow={(i) =>
                  mutateAirline(safeActiveIndex, (a) =>
                    a.freightCharges.splice(i, 1),
                  )
                }
                copySources={otherAirlineSources(safeActiveIndex)}
                onCopy={(source) =>
                  mutateAirline(
                    safeActiveIndex,
                    (a) =>
                      (a.freightCharges = JSON.parse(
                        JSON.stringify(source.airline.freightCharges),
                      )),
                  )
                }
                subtotals={[
                  {
                    label: "Subtotal",
                    value: money(
                      sumRows(activeAirline.freightCharges, totalWeight),
                    ),
                  },
                ]}
                addLabel="Add charge"
                singletonCharges={airFreightChargeIds}
                singletonMessage={AIR_FREIGHT_DUPLICATE_MESSAGE}
                readOnly={readOnly}
                showErrors={submitAttempted}
              />

              {/* Itinerary — one airline can carry several flight options
                (different schedules); charges above are shared by all of them. */}
              <div
                style={{
                  border: `1px solid ${CARD_BORDER}`,
                  borderRadius: 14,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    flexWrap: "wrap",
                    padding: "15px 18px",
                    borderBottom: `1px solid ${ROW_BORDER}`,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 11 }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 9,
                        background: "#FFF3D2",
                        color: ACCENT_DARK,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Plane size={14} />
                    </div>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: "#20242B",
                      }}
                    >
                      Itinerary
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {!readOnly && (
                      <CopyFromMenu
                        sources={otherAirlineSources(safeActiveIndex)}
                        onCopy={(source) => {
                          mutateAirline(safeActiveIndex, (a) => {
                            const cloned = JSON.parse(
                              JSON.stringify(
                                source.airline.flightOptions || [],
                              ),
                            );
                            a.flightOptions = cloned.map((opt) => ({
                              ...opt,
                              segments: opt.segments.map((s) => ({
                                ...s,
                                carrier: a.carrierCode || s.carrier,
                                flight: "",
                              })),
                            }));
                          });
                          setActiveOptionIndex(0);
                        }}
                      />
                    )}
                    <span
                      style={{
                        background: "#F4F1EA",
                        borderRadius: 999,
                        padding: "5px 11px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#6E685C",
                      }}
                    >
                      {transitMinutes > 0
                        ? `Total transit ${durationLabel(transitMinutes)}`
                        : `${activeOption.segments.length} segment(s)`}
                    </span>
                  </div>
                </div>

                {/* Flight option tabs — each option is a distinct schedule for
                  this same airline/price. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                    padding: "12px 18px 0",
                  }}
                >
                  {activeAirline.flightOptions.map((opt, i) => {
                    const active = i === safeOptionIndex;
                    const incomplete =
                      submitAttempted &&
                      !isItineraryComplete(
                        withLockedEnds(
                          opt.segments,
                          originAirport,
                          destAirport,
                        ),
                      );
                    return (
                      <div
                        key={i}
                        onClick={() => setActiveOptionIndex(i)}
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          borderRadius: 999,
                          padding: "5px 8px 5px 12px",
                          fontSize: 11.5,
                          fontWeight: 600,
                          border: incomplete
                            ? `1px solid ${ERROR_COLOR}`
                            : active
                              ? `1px solid ${ACCENT}`
                              : `1px solid ${CARD_BORDER}`,
                          background: active ? "#FFF3D2" : "#fff",
                          color: incomplete
                            ? ERROR_COLOR
                            : active
                              ? ACCENT_DARK
                              : "#4A463E",
                        }}
                      >
                        <span>Flight option {i + 1}</span>
                        {incomplete && (
                          <span
                            title="Itinerary incomplete"
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: ERROR_COLOR,
                              flex: "none",
                            }}
                          />
                        )}
                        {!readOnly &&
                          activeAirline.flightOptions.length > 1 && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFlightOption(i);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                opacity: 0.7,
                              }}
                              title="Remove flight option"
                            >
                              <X size={11} />
                            </span>
                          )}
                      </div>
                    );
                  })}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={addFlightOption}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        cursor: "pointer",
                        border: `1px dashed ${ACCENT}`,
                        color: ACCENT_DARK,
                        borderRadius: 999,
                        padding: "5px 11px",
                        fontSize: 11.5,
                        fontWeight: 600,
                        background: "transparent",
                      }}
                    >
                      <Plus size={12} />
                      Add flight option
                    </button>
                  )}
                </div>

                {/* LAT/TOA — one value for the whole flight option (applies to
                  every leg/segment below), not entered per segment. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    flexWrap: "wrap",
                    padding: "12px 18px 0",
                  }}
                >
                  <div style={{ width: 140 }}>
                    <label className={LABEL_CLS}>LAT (Min)</label>
                    <input
                      type="text"
                      value={activeOption.lat || ""}
                      placeholder="251"
                      className={INPUT_CLS}
                      disabled={readOnly}
                      inputMode="numeric"
                      onChange={(e) =>
                        mutateActiveOption(
                          (opt) => (opt.lat = e.target.value),
                        )
                      }
                    />
                  </div>
                  <div style={{ width: 140 }}>
                    <label className={LABEL_CLS}>TOA (Min)</label>
                    <input
                      type="text"
                      value={activeOption.toa || ""}
                      placeholder="251"
                      className={INPUT_CLS}
                      disabled={readOnly}
                      inputMode="numeric"
                      onChange={(e) =>
                        mutateActiveOption(
                          (opt) => (opt.toa = e.target.value),
                        )
                      }
                    />
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px 18px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  {finalSegments.map((seg, i) => (
                    <SegmentCard
                      key={i}
                      seg={seg}
                      index={i}
                      isFirst={i === 0}
                      isLast={i === finalSegments.length - 1}
                      canRemove={finalSegments.length > 1}
                      airLineList={airLineList}
                      airPort={airPort}
                      originAirport={originAirport}
                      destAirport={destAirport}
                      layoverLabel={
                        i < finalSegments.length - 1
                          ? (() => {
                              const mins = minutesBetween(
                                finalSegments[i].arrival,
                                finalSegments[i + 1].departure,
                              );
                              return mins > 0
                                ? `${durationLabel(mins)} layover in ${airportCode(finalSegments[i].destination)}`
                                : "";
                            })()
                          : ""
                      }
                      onUpdate={(field, value) =>
                        mutateActiveOption((opt) => {
                          opt.segments[i][field] = value;
                          if (
                            field === "destination" &&
                            i < opt.segments.length - 1
                          ) {
                            opt.segments[i + 1].origin = value;
                          }
                        })
                      }
                      onRemove={() =>
                        mutateActiveOption((opt) => {
                          if (opt.segments.length > 1)
                            opt.segments.splice(i, 1);
                        })
                      }
                      readOnly={readOnly}
                      showErrors={submitAttempted}
                    />
                  ))}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() =>
                        mutateActiveOption((opt) =>
                          opt.segments.push(
                            makeSegment(
                              activeAirline.carrierCode,
                              opt.segments[opt.segments.length - 1]
                                ?.destination || "",
                            ),
                          ),
                        )
                      }
                      style={ADD_SECTION_BTN_STYLE}
                    >
                      <Plus size={15} />
                      Add segment
                    </button>
                  )}
                </div>
              </div>
            </div>

            {formError && (
              <p style={{ color: "#ef4444", fontSize: 13 }}>{formError}</p>
            )}
            {!formError && hasBlockingChargeRowIssue && (
              <p style={{ color: "#ef4444", fontSize: 13 }}>
                {FREIGHT_ROW_ISSUE_MESSAGE}
              </p>
            )}

            {/* Sticky footer totals */}
            <div
              className="flex flex-wrap items-center justify-between gap-3"
              style={{
                position: "sticky",
                bottom: 0,
                background: "#fff",
                border: `1px solid ${CARD_BORDER}`,
                borderRadius: 16,
                padding: "16px 18px",
                boxShadow: "0 -6px 24px rgba(32,36,43,0.06)",
              }}
            >
              <div>
                <div className="text-[10.5px] uppercase tracking-wide font-semibold text-gray-400">
                  Origin
                </div>
                <div
                  style={{
                    fontFamily: "monospace",
                    fontSize: 15,
                    fontWeight: 600,
                    marginTop: 3,
                  }}
                >
                  {money(
                    odAmount(activeAirline.odCharges, "origin", totalWeight),
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-wide font-semibold text-gray-400">
                  Destination
                </div>
                <div
                  style={{
                    fontFamily: "monospace",
                    fontSize: 15,
                    fontWeight: 600,
                    marginTop: 3,
                  }}
                >
                  {money(
                    odAmount(
                      activeAirline.odCharges,
                      "destination",
                      totalWeight,
                    ),
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-wide font-semibold text-gray-400">
                  Freight &amp; surcharges
                </div>
                <div
                  style={{
                    fontFamily: "monospace",
                    fontSize: 15,
                    fontWeight: 600,
                    marginTop: 3,
                  }}
                >
                  {money(sumRows(activeAirline.freightCharges, totalWeight))}
                </div>
              </div>
              <div
                style={{
                  background: "#FFF3D2",
                  border: `1px solid ${TINT_BORDER}`,
                  borderRadius: 11,
                  padding: "11px 15px",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                <div
                  className="text-[10.5px] uppercase tracking-wide font-bold"
                  style={{ color: "#8C6A12", whiteSpace: "nowrap" }}
                >
                  Grand total
                </div>
                <div
                  style={{
                    fontFamily: "monospace",
                    fontSize: 18,
                    fontWeight: 600,
                    color: "#20242B",
                    whiteSpace: "nowrap",
                  }}
                >
                  {money(airlineTotals(activeAirline, totalWeight).grand)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 9, marginLeft: "auto" }}>
                {readOnly ? (
                  <button
                    type="button"
                    onClick={() => {
                      navigate("/QueriesKAM");
                      dispatch(clearManualQuote());
                    }}
                    className="font-semibold"
                    style={{
                      border: `1px solid ${CARD_BORDER}`,
                      background: "#fff",
                      color: "#4A463E",
                      borderRadius: 10,
                      padding: "11px 16px",
                      fontSize: 13.5,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Back to Queries
                  </button>
                ) : (
                  <>
                    {/* <button
                    type="button"
                    onClick={handleSaveActiveAirline}
                    className="font-semibold"
                    style={{ border: `1px solid ${CARD_BORDER}`, background: "#fff", color: "#4A463E", borderRadius: 10, padding: "11px 16px", fontSize: 13.5, cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    Save this airline
                  </button> */}
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading || hasBlockingChargeRowIssue}
                      title={
                        hasBlockingChargeRowIssue
                          ? FREIGHT_ROW_ISSUE_MESSAGE
                          : undefined
                      }
                      className="font-bold"
                      style={{
                        border: "none",
                        background: ACCENT,
                        color: "#20242B",
                        borderRadius: 10,
                        padding: "11px 18px",
                        fontSize: 13.5,
                        cursor:
                          loading || hasBlockingChargeRowIssue
                            ? "not-allowed"
                            : "pointer",
                        whiteSpace: "nowrap",
                        opacity: loading || hasBlockingChargeRowIssue ? 0.5 : 1,
                      }}
                    >
                      {submitLabel}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* All airlines summary */}
            {airlines.length > 1 && (
              <div
                style={{
                  border: `1px solid ${CARD_BORDER}`,
                  borderRadius: 16,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "15px 18px",
                    borderBottom: `1px solid ${ROW_BORDER}`,
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    All airlines in this quotation
                  </div>
                </div>
                <div
                  style={{
                    padding: "14px 18px 18px",
                    overflowX: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(150px,1.3fr) minmax(150px,1.2fr) 92px repeat(4, minmax(92px,1fr))",
                      gap: 10,
                      padding: "0 14px",
                      minWidth: 1000,
                    }}
                  >
                    {[
                      "Airline",
                      "Routing",
                      "Transit",
                      "Origin",
                      "Destination",
                      "Freight & surcharges",
                      "Grand total",
                    ].map((h, i) => (
                      <span
                        key={i}
                        className="text-[10.5px] uppercase tracking-wide font-semibold text-gray-400"
                        style={{ textAlign: i > 2 ? "right" : "left" }}
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                  {airlines.map((al, i) => {
                    // Representative routing/transit is shown for the airline's
                    // first flight option — every option shares the same price.
                    const segs = withLockedEnds(
                      al.flightOptions?.[0]?.segments,
                      originAirport,
                      destAirport,
                    );
                    const routing = segs.length
                      ? [
                          ...segs.map((s) => airportCode(s.origin)),
                          airportCode(segs[segs.length - 1]?.destination),
                        ].join(" → ")
                      : "—";
                    const transit = segs.length
                      ? durationLabel(
                          minutesBetween(
                            segs[0].departure,
                            segs[segs.length - 1].arrival,
                          ),
                        )
                      : "—";
                    const totals = airlineTotals(al, totalWeight);
                    const sel = i === safeActiveIndex;
                    return (
                      <div
                        key={i}
                        onClick={() => selectAirline(i)}
                        style={{
                          cursor: "pointer",
                          display: "grid",
                          gridTemplateColumns:
                            "minmax(150px,1.3fr) minmax(150px,1.2fr) 92px repeat(4, minmax(92px,1fr))",
                          gap: 10,
                          alignItems: "center",
                          padding: "11px 14px",
                          borderRadius: 10,
                          minWidth: 1000,
                          background: sel ? "#FFFAEF" : "#fff",
                          border: sel
                            ? `1px solid ${TINT_BORDER}`
                            : `1px solid ${ROW_BORDER}`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 11,
                            minWidth: 0,
                          }}
                        >
                          <AirlineBadge
                            code={al.carrierCode || "—"}
                            logo={carrierLogo(al.carrierCode)}
                            active={sel}
                          />
                          <div style={{ minWidth: 0 }}>
                            <div className="text-[13.5px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                              {carrierLabel(al.carrierCode) ||
                                al.carrierCode ||
                                `New airline ${i + 1}`}
                            </div>
                            <div className="text-[11.5px] text-gray-400 mt-0.5">
                              {al.serviceType} · {al.flightOptions.length}{" "}
                              flight option
                              {al.flightOptions.length > 1 ? "s" : ""}
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            fontFamily: "monospace",
                            fontSize: 12.5,
                            color: "#4A463E",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {routing}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6E685C",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {transit}
                        </div>
                        <div
                          style={{
                            textAlign: "right",
                            fontFamily: "monospace",
                            fontSize: 12.5,
                            color: "#4A463E",
                          }}
                        >
                          {money(totals.origin)}
                        </div>
                        <div
                          style={{
                            textAlign: "right",
                            fontFamily: "monospace",
                            fontSize: 12.5,
                            color: "#4A463E",
                          }}
                        >
                          {money(totals.destination)}
                        </div>
                        <div
                          style={{
                            textAlign: "right",
                            fontFamily: "monospace",
                            fontSize: 12.5,
                            color: "#4A463E",
                          }}
                        >
                          {money(totals.freight)}
                        </div>
                        <div
                          style={{
                            textAlign: "right",
                            fontFamily: "monospace",
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          {money(totals.grand)}
                        </div>
                      </div>
                    );
                  })}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 12,
                      padding: "10px 14px 0",
                      borderTop: `1px solid ${ROW_BORDER}`,
                      marginTop: 4,
                      minWidth: 1000,
                    }}
                  >
                    <div className="text-[12px] uppercase tracking-wide font-semibold text-gray-400">
                      All airlines combined
                    </div>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: 15,
                        fontWeight: 600,
                      }}
                    >
                      {money(combinedGrandTotal)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {savedState && (
              <div
                style={{
                  background: "#EFF8F1",
                  border: "1px solid #CDE7D5",
                  color: "#1F6B3B",
                  borderRadius: 12,
                  padding: "12px 16px",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {savedMsg}
              </div>
            )}

            {/* Quotation history — view-only mode only. Lets KAM open a past
              snapshot of this quote (read-only) via getManualQuoteHistory. */}
            {readOnly && manualQuoteDetails?._id && (
              <div
                style={{
                  border: `1px solid ${CARD_BORDER}`,
                  borderRadius: 16,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "15px 18px",
                    borderBottom: `1px solid ${ROW_BORDER}`,
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    Quotation history
                  </div>
                </div>

                <div
                  style={{
                    padding: "14px 18px 18px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {historyLoading ? (
                    <div style={{ fontSize: 13, color: "#6E685C" }}>
                      Loading history...
                    </div>
                  ) : historyEntries.length === 0 ? (
                    <div style={{ fontSize: 13, color: "#6E685C" }}>
                      No history available for this quotation.
                    </div>
                  ) : (
                    historyEntries.map((entry, idx) => {
                      const isLatest = idx === 0;
                      const isViewed =
                        viewedVersion === null
                          ? isLatest
                          : viewedVersion === entry.version;
                      return (
                        <div
                          key={entry.version}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            flexWrap: "wrap",
                            padding: "12px 16px",
                            borderRadius: 12,
                            border: isViewed
                              ? `1px solid ${ACCENT}`
                              : `1px solid ${ROW_BORDER}`,
                            background: isViewed ? "#FFFAEF" : "#fff",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            <Clock size={14} style={{ color: "#9A9384" }} />
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: 13,
                                color: "#20242B",
                              }}
                            >
                              {dayjs(entry.updatedAt).format("DD MMM YYYY")}
                            </span>
                            <span style={{ fontSize: 12.5, color: "#9A9384" }}>
                              {dayjs(entry.updatedAt).format("HH:mm:ss")}
                            </span>
                            {isLatest && (
                              <span
                                style={{
                                  background: ACCENT,
                                  color: "#20242B",
                                  fontSize: 10.5,
                                  fontWeight: 700,
                                  borderRadius: 999,
                                  padding: "3px 9px",
                                  letterSpacing: 0.4,
                                }}
                              >
                                LATEST
                              </span>
                            )}
                            <span style={{ fontSize: 12.5, color: "#6E685C" }}>
                              {entry.updatedBy?.full_name || "—"}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              isLatest
                                ? handleBackToLatest()
                                : handleViewHistoryState(entry)
                            }
                            style={{
                              border: `1px solid ${CARD_BORDER}`,
                              background: isViewed ? ACCENT : "#fff",
                              color: isViewed ? "#20242B" : "#4A463E",
                              borderRadius: 8,
                              padding: "8px 14px",
                              fontSize: 12.5,
                              fontWeight: 600,
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            View state
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>

                {viewedVersion !== null && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      padding: "12px 18px",
                      background: "#FFF8E7",
                      borderTop: `1px solid ${TINT_BORDER}`,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12.5,
                        color: ACCENT_DARK,
                        fontWeight: 600,
                      }}
                    >
                      Viewing read-only snapshot of this
                      quotation.
                    </span>
                    <span
                      onClick={handleBackToLatest}
                      style={{
                        fontSize: 12.5,
                        color: ACCENT_DARK,
                        fontWeight: 700,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Back to latest
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </MainContainer>
    </LocalizationProvider>
  );
};

export default ManualQuoteHtmlUI;
